import FilterPopoverTab from '@/Enums/Attribution/FootTraffic/FilterPopoverTabs.enum';
import TabSelectLabel from '@/Enums/TabSelect/Label.enum';
import { IFootTrafficLineItemControlOption } from '@/Interfaces/Attribution/State.interface';
import { IControlOption } from '@/Interfaces/ControlOption.interface';
import AddFilterButton from '@/components/Filter/AddFilterButton';
import { LocationsTabButton, PlacementsTabButton } from '@/components/Filter/TabButtons';
import TabFilterPopover from '@/components/Filter/TabFilterPopover';
import TabPanel from '@/components/TabPanel';
import TabSelect from '@/components/TabSelect';
import { FILTERS_BASE_TAB_ID } from '@/constants/attribution';
import { areArraysOfObjectsEqual } from '@/helpers/comparators';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectFootTrafficFilters,
  selectFootTrafficLineItemFilterOptions,
  selectFootTrafficLocationFilterOptions,
} from '@/store/selectors/attribution/footTraffic.selectors';
import {
  getFootTrafficLineItemOptions,
  getFootTrafficLocationOptions,
  setFilters,
} from '@/store/slices/attribution/footTraffic.slice';
import { a11yProps } from '@/utils/tabProps';
import { Tab, Tabs, popoverClasses, tabClasses } from '@mui/material';
import { MouseEvent, SyntheticEvent, useCallback, useMemo, useState } from 'react';

const BASE_TAB_ID = 'foot-traffic-filter-popover';

const FilterPopover = () => {
  const dispatch = useAppDispatch();

  //#region state, selectors
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLButtonElement | null>(null);
  const filtersOpen = useMemo<boolean>(() => Boolean(filterAnchorEl), [filterAnchorEl]);
  const [currentTab, setCurrentTab] = useState<FilterPopoverTab>(FilterPopoverTab.Locations);

  const { locations, loading: loadingLocations } = useAppSelector(selectFootTrafficLocationFilterOptions);
  const { lineItems, loading: loadingLineItems } = useAppSelector(selectFootTrafficLineItemFilterOptions);
  const {
    advertisers: filterAdvertisers,
    locations: filterLocations,
    lineItems: filterLineItems,
  } = useAppSelector(selectFootTrafficFilters);

  const [pendingLocations, setPendingLocations] = useState<IControlOption[]>([]);
  const [pendingLineItems, setPendingLineItems] = useState<IFootTrafficLineItemControlOption[]>([]);

  const activeFilterCount = useMemo(
    () => (filterLocations.length ? 1 : 0) + (filterLineItems.length ? 1 : 0),
    [filterLocations.length, filterLineItems.length],
  );
  //#endregion

  //#region memo
  const filtersId = useMemo(() => (filtersOpen ? 'additional-filters-popover' : undefined), [filtersOpen]);

  const commonTabPanelProps = useMemo(
    () => ({
      className: 'w-full',
      baseTabId: BASE_TAB_ID,
      value: currentTab,
    }),
    [currentTab],
  );
  //#endregion

  //#region callback
  const resetPendingValues = useCallback(() => {
    setPendingLocations(filterLocations);
    setPendingLineItems(filterLineItems);
  }, [filterLocations, filterLineItems]);

  const loadLocations = useCallback(() => {
    dispatch(getFootTrafficLocationOptions({}));
  }, [dispatch]);

  const loadLineItems = useCallback(() => {
    dispatch(getFootTrafficLineItemOptions({}));
  }, [dispatch]);

  const handleOpen = useCallback(
    ({ currentTarget }: MouseEvent<HTMLButtonElement>) => {
      resetPendingValues();
      setFilterAnchorEl(currentTarget);
      loadLocations();
    },
    [resetPendingValues, loadLocations],
  );

  const handleClose = useCallback(() => {
    setFilterAnchorEl(null);
    resetPendingValues();
    setCurrentTab(FilterPopoverTab.Locations);
  }, [setFilterAnchorEl, resetPendingValues]);

  const handleTabChange = useCallback(
    (_: SyntheticEvent, newValue: FilterPopoverTab) => {
      setCurrentTab(newValue);
      if (newValue === FilterPopoverTab.Locations) {
        loadLocations();
      }
      if (newValue === FilterPopoverTab.Placements) {
        loadLineItems();
      }
    },
    [loadLocations, loadLineItems],
  );

  const updateLocationValue = useCallback((updatedLocations: IControlOption[] = []) => {
    setPendingLocations(updatedLocations);
  }, []);

  const updateLineItemsValue = useCallback((updatedLineItems: IFootTrafficLineItemControlOption[] = []) => {
    setPendingLineItems(updatedLineItems);
  }, []);

  const handleSubmit = useCallback(() => {
    if (
      areArraysOfObjectsEqual(filterLocations, pendingLocations) &&
      areArraysOfObjectsEqual(filterLineItems, pendingLineItems)
    ) {
      handleClose();
      return;
    }

    dispatch(setFilters({ locations: pendingLocations, lineItems: pendingLineItems }));
    handleClose();
  }, [dispatch, pendingLocations, filterLocations, pendingLineItems, filterLineItems, handleClose]);
  //#endregion

  return (
    <>
      <AddFilterButton
        onClick={handleOpen}
        describedBy={filtersId}
        disabled={!filterAdvertisers.length}
        badgeCount={activeFilterCount}
      />
      <TabFilterPopover
        id={filtersId}
        anchorEl={filterAnchorEl}
        open={filtersOpen}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          [`& .${popoverClasses.paper}`]: {
            width: 1000,
            maxWidth: 1000,
          },
        }}
      >
        <Tabs
          value={currentTab}
          orientation='vertical'
          onChange={handleTabChange}
          aria-label='filter tabs'
          sx={{
            minHeight: 'unset',
            background: '#1B43320A',
            padding: '16px',
            minWidth: '230px',
            [`& .${tabClasses.root}`]: {
              paddingX: 0,
              minHeight: 'unset',
              justifyContent: 'flex-start',
              paddingLeft: '16px',
              paddingRight: '16px',
            },
            [`& .${tabClasses.selected}`]: {
              background: '#1B43321F',
            },
          }}
          slotProps={{
            indicator: {
              hidden: true,
            },
          }}
        >
          <Tab
            value={FilterPopoverTab.Locations}
            component={LocationsTabButton}
            disabled={!filterAdvertisers.length}
            {...a11yProps(FilterPopoverTab.Locations, FILTERS_BASE_TAB_ID)}
          />
          <Tab
            value={FilterPopoverTab.Placements}
            component={PlacementsTabButton}
            disabled={!filterAdvertisers.length}
            {...a11yProps(FilterPopoverTab.Placements, FILTERS_BASE_TAB_ID)}
          />
        </Tabs>
        <TabPanel {...commonTabPanelProps} index={FilterPopoverTab.Locations}>
          <TabSelect
            label={TabSelectLabel.Location}
            options={locations}
            value={pendingLocations}
            onChange={updateLocationValue}
            onClose={handleClose}
            onSubmit={handleSubmit}
            loading={loadingLocations}
          />
        </TabPanel>
        <TabPanel {...commonTabPanelProps} index={FilterPopoverTab.Placements}>
          <TabSelect
            label={TabSelectLabel.Placement}
            options={lineItems}
            value={pendingLineItems}
            onChange={updateLineItemsValue}
            onClose={handleClose}
            onSubmit={handleSubmit}
            loading={loadingLineItems}
            grouped
          />
        </TabPanel>
      </TabFilterPopover>
    </>
  );
};

export default FilterPopover;
